import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { fadeUp, viewportOnce, stagger } from "../lib/motion";

const SECTIONS = [
    {
        h: "1. Acceptance of these terms",
        p: [
            "By accessing or using the BuildEase website and platform, you agree to these Terms & Conditions. If you do not agree, please do not use the platform.",
        ],
    },
    {
        h: "2. What BuildEase is — and is not",
        p: [
            "BuildEase is a residential construction intelligence platform that helps homeowners understand construction information — architectural plans, quantities, cost estimates, contractor quotations, materials, compliance considerations and design concepts.",
            "BuildEase is not a construction contractor, architect, quantity surveyor, engineer or attorney. BuildEase does not provide professional advice, professional certification, regulatory approval or guarantees of construction outcomes. Outputs depend on the information supplied and are decision-support tools to be reviewed with appropriate qualified professionals.",
        ],
    },
    {
        h: "3. Accounts and projects",
        p: [
            "You are responsible for the accuracy of the information you provide and for keeping your account credentials secure. A BuildEase plan belongs to an independent project, not an account — one account may hold several projects, each with its own plan.",
        ],
    },
    {
        h: "4. Plans, billing and access",
        p: [
            "Plans are once-off payments per project (Free, Standard or Premium as published on the pricing page). Paid projects include lifetime access to that project, with updates within the plan included as they are released.",
            "Revised plans, extra drawings and amendments for the same project do not trigger a new charge. A genuinely different property requires its own project and plan.",
            "Payments are processed by our payment processing provider (PayFast is the intended provider at launch). BuildEase never stores card details. Pricing, availability and feature scope may change as the platform develops; the version published on the pricing page applies at the time of purchase.",
        ],
    },
    {
        h: "5. Uploaded content",
        p: [
            "You retain ownership of the documents and information you upload. You grant BuildEase the limited right to process them to operate your project and provide the platform's insights. You confirm you have the right to submit any documents you upload.",
        ],
    },
    {
        h: "6. AI-generated information",
        p: [
            "BuildEase uses AI to support construction insights. AI-generated information may contain errors and should be reviewed where appropriate. Information is labelled Verified, Estimated or Assumed; estimates are not guaranteed costs, formal quantity surveyor estimates or binding quotations.",
        ],
    },
    {
        h: "7. Acceptable use",
        p: [
            "You agree not to misuse the platform — including attempting to access other users' projects, probing or disrupting the service, uploading content you have no right to use, or using the platform unlawfully.",
        ],
    },
    {
        h: "8. Liability",
        p: [
            "To the extent permitted by law, BuildEase is not liable for decisions made, or losses suffered, in reliance on platform outputs without appropriate professional review. Nothing in these terms limits liability that cannot be limited under South African law.",
        ],
    },
    {
        h: "9. Governing law",
        p: [
            "These terms are governed by the laws of the Republic of South Africa.",
        ],
    },
    {
        h: "10. Contact",
        p: [
            "Questions about these terms: support@buildease.co.za.",
        ],
    },
];

const TermsPage = () => (
    <div className="min-h-screen bg-ink text-white">
        <Seo
            title="Terms & Conditions | BuildEase"
            description="The terms governing use of the BuildEase platform: accounts, project-based plans, billing, AI-generated information, acceptable use and liability."
        />
        <Navbar />
        <main>
            <section className="relative overflow-hidden bg-ink pt-[128px] lg:pt-[168px]">
                <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
                    <motion.div variants={fadeUp} initial="hidden" animate="show">
                        <p className="flex items-center gap-3 font-mono text-[13px] tracking-[0.3em] text-mist">
                            <span className="h-1.5 w-1.5 bg-accent" />
                            TERMS &amp; CONDITIONS
                        </p>
                        <h1
                            data-testid="terms-headline"
                            className="mt-8 font-display text-4xl font-semibold leading-[1.08] text-white md:text-5xl"
                        >
                            Clear terms for a clear platform
                            <span className="text-accent">.</span>
                        </h1>
                        <p className="mt-4 font-mono text-[13px] tracking-[0.22em] text-cad">
                            LAST UPDATED — NOVEMBER 2026
                        </p>
                    </motion.div>
                </div>
            </section>

            <section className="relative py-14 lg:py-20">
                <div className="mx-auto max-w-[900px] px-6 md:px-10 lg:px-16">
                    <motion.div
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                    >
                        {SECTIONS.map((s) => (
                            <motion.div key={s.h} variants={fadeUp} className="mt-10 first:mt-0">
                                <h2 className="font-display text-2xl font-semibold text-white">
                                    {s.h}
                                </h2>
                                {s.p.map((para, i) => (
                                    <p key={i} className="mt-4 text-[16px] leading-relaxed text-mist">
                                        {para}
                                    </p>
                                ))}
                            </motion.div>
                        ))}
                        <motion.p variants={fadeUp} className="mt-14 border-t border-white/10 pt-8 text-[15px] leading-relaxed text-mist">
                            Related:{" "}
                            <Link to="/privacy" data-testid="terms-privacy-link" className="text-accent hover:text-[#ff7026]">
                                Privacy Policy
                            </Link>{" "}
                            ·{" "}
                            <Link to="/pricing" data-testid="terms-pricing-link" className="text-accent hover:text-[#ff7026]">
                                Pricing
                            </Link>
                        </motion.p>
                    </motion.div>
                </div>
            </section>
        </main>
        <Footer />
    </div>
);

export default TermsPage;
